<template>
  <div
    class="drawer-root"
    :data-elevated-layer="elevated && isBottom ? '' : undefined"
  >
    <Transition name="drawer-backdrop">
      <div
        v-if="open && backdrop"
        drawer-backdrop
        :class="backdropClasses"
        @click="closeDrawer"
      ></div>
    </Transition>
    <Transition name="drawer-slide" @after-leave="onClosed">
      <div
        v-if="open"
        ref="panelRef"
        role="dialog"
        aria-modal="true"
        :aria-label="ariaLabel || title || undefined"
        :class="panelClasses"
        :style="isBottom ? dragStyle : undefined"
        @touchstart="isBottom ? onTouchStart($event) : undefined"
        @touchmove="isBottom ? onTouchMove($event) : undefined"
        @touchend="isBottom ? onTouchEnd() : undefined"
      >
        <div
          v-if="includeHeader"
          class="absolute bg-white text-white w-full z-10 text-xs"
        >
          <slot name="drawerHeader"> </slot>
        </div>
        <!-- `min-w-0` because this is a grid item, whose automatic minimum size
             is its content's, not zero. Without it a title slot containing
             anything that will not wrap -- a horizontally scrolling row of
             chips, a long unbroken string -- sizes this row to its content and
             widens the whole panel past the viewport. The panel's own
             `overflow-x-hidden` then hides the excess rather than scrolling it,
             and focusing something inside the overflow scrolls the panel
             sideways with no way back, carrying the title and body off screen. -->
        <div
          :class="[
            'p-4 relative rounded-t-lg bg-white z-20 min-w-0',
            { 'mt-9 header-box-shadow': includeHeader }
          ]"
        >
          <slot name="title">
            <div class="flex gap-3">
              <div v-if="backArrow" class="cursor-pointer" @click="closeDrawer">
                <CaretLeft class="h-6" />
              </div>
              <h5
                class="mb-4 inline-flex items-center text-base font-semibold text-gray-900"
              >
                {{ title }}
              </h5>
            </div>
          </slot>
          <button
            v-if="!backArrow"
            type="button"
            class="absolute right-2.5 top-2.5 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-transparent text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white cursor-pointer"
            @click="closeDrawer"
          >
            <CloseIcon class="w-4" />
            <span class="sr-only">Close menu</span>
          </button>
        </div>
        <div class="overflow-y-auto">
          <slot></slot>
        </div>
        <div v-if="!noFooter" :class="footerClasses">
          <slot name="footer"></slot>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

import { useSwipeDismiss } from "@/composables/useSwipeDismiss";
import CaretLeft from "@/images/CaretLeft.svg?component";
import CloseIcon from "@/images/x.svg?component";

const SHEET_OPEN_CLASS = "sheet-open";

/**
 * Depth counters for the page-level effects, shared across every live instance:
 * the appointment details drawer renders the cancel drawer as a SIBLING, so two
 * are open at once and the inner one's teardown must not restore the page while
 * the outer is still up.
 *
 * Parked on `window` rather than in module scope because module scope is not
 * reliably one instance: under Vitest each `mount()` of this SFC got its own
 * copy of a module-level `let`, so a depth counter silently reset to 0 per
 * drawer and the sibling case could not be tested at all (it appeared to pass
 * only because restoring "hidden" over "hidden" is indistinguishable). One
 * window-scoped record behaves the same in the browser and under test.
 */
type DrawerDepth = {
  drawers: number;
  sheets: number;
  restoreOverflow: string | null;
};
const depth: DrawerDepth = ((
  window as unknown as { __seraDrawerDepth?: DrawerDepth }
).__seraDrawerDepth ??= { drawers: 0, sheets: 0, restoreOverflow: null });

const emit = defineEmits(["hide-drawer", "show-drawer"]);
const props = withDefaults(
  defineProps<{
    title?: string;
    includeHeader?: boolean;
    backArrow?: boolean;
    noFooter?: boolean;
    backdrop?: boolean;
    // Lifts a bottom sheet above every dialog band, for one opened from inside
    // a modal. Mirrors BottomSheet's prop of the same name, and like it marks
    // the root as an elevated layer so popovers beneath stay open while the
    // user presses inside it (lib/popoverOutsidePress). No effect on "right".
    elevated?: boolean;
    // "bottom" anchors the panel as a full-width sheet. Defaults to "right" so
    // every existing consumer renders exactly as before.
    placement?: "right" | "bottom";
    // Accessible name for the dialog. Declared as a prop rather than left to
    // attribute fallthrough, which would land it on `.drawer-root` instead of
    // the element that actually carries role="dialog". Falls back to `title`,
    // which several consumers already set.
    ariaLabel?: string;
  }>(),
  {
    title: "",
    backdrop: true,
    backArrow: false,
    elevated: false,
    includeHeader: false,
    noFooter: false,
    placement: "right",
    ariaLabel: ""
  }
);

const isBottom = computed(() => props.placement === "bottom");

// Both branches are complete literal class strings: Tailwind's scanner reads
// source text, so an interpolated `${placement}-0` would never be generated.
//
// `drawer` and `grid` must survive on BOTH branches -- `.drawer.grid` is the
// selector the teardown strand guard reports on (Scheduler/SchedulePage.vue)
// and the one ~50 Vitest/Cypress/Playwright assertions match.
//
// Bottom overrides the panel's grid rows with a Tailwind arbitrary value rather
// than a sibling SCSS rule: utilities are !important here, so this reliably
// beats `.drawer.grid`'s 1fr 14fr 1fr, which is tuned for a full-height panel.
const PANEL_RIGHT =
  "fixed top-0 right-0 z-60 h-100dvh w-full md:max-h-97dvh rounded-lg max-md:rounded-none md:mr-4 md:mt-4 md:w-[540px] overflow-x-hidden bg-white dark:bg-gray-800 drawer grid";
// Bottom placement sits at 1002/1003 rather than the 50/60 that right placement
// uses, because the mobile nav drawer is z-[1001] (Sidebar/Sidebar.vue) and is
// fixed only below `md` -- exactly the width a sheet is for. At 60 a sheet would
// render underneath it.
//
// 1002-1008 is the one empty band in the current scale, so this lands above the
// nav drawer while staying below SpinnerBackdrop (1009), both modals (1020/1030),
// FlyoutPanel (1100), the datepicker (1200), selects (9999) and the env banner
// (19001) -- i.e. above page content and below dialogs, which is the ordering
// TB-2193's band map specifies (sheets above nav drawer, below dialogs).
//
// These are placeholders for that map, not a design: when the z-index migration
// lands, both placements move onto named bands and these numbers go away. Right
// placement is deliberately left on 50/60 so existing consumers are untouched.
//
// The horizontal insets are the sheet's own because it is `fixed`: it resolves
// against the viewport, so the shell's safe-area padding (tailwind_components.css)
// cannot reach it. They matter in landscape, where the notch eats one edge.
// Right placement does not need them -- it is inset from the edge already at md
// and above, and full-bleed only below md where the notch is on the short edge.
const PANEL_BOTTOM =
  // `max-h-70dvh` is paired with a `vh` fallback in `.drawer--bottom` below; it
  // stays here because specs and stories reference the utility by name, and
  // because the two cannot conflict -- see the comment on that rule.
  //
  // The z-index is not here: it comes from BANDS below, because a sheet opened
  // from inside a dialog needs a different one.
  "fixed bottom-0 left-0 right-0 w-full max-h-70dvh rounded-t-lg overflow-x-hidden bg-white dark:bg-gray-800 drawer grid grid-rows-[auto_1fr_auto] drawer--bottom pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]";

// Complete literals for Tailwind's scanner. `elevated` matches BottomSheet's
// band exactly: a sheet opened from inside a Seranova Modal (1020/1030) or a
// Bootstrap one (1050) renders underneath it on the default band, which reads
// as the sheet silently failing to open.
const BANDS = {
  sheet: { panel: "z-[1003]", backdrop: "z-[1002]" },
  elevated: { panel: "z-[10001]", backdrop: "z-[10000]" }
};

const band = computed(() => (props.elevated ? BANDS.elevated : BANDS.sheet));

const panelClasses = computed(() => {
  if (!isBottom.value) return PANEL_RIGHT;
  // With no footer to carry it, the panel itself must clear the home indicator.
  const base = `${PANEL_BOTTOM} ${band.value.panel}`;
  return props.noFooter ? `${base} pb-[env(safe-area-inset-bottom)]` : base;
});

const backdropClasses = computed(() =>
  isBottom.value
    ? `fixed inset-0 ${band.value.backdrop} bg-gray-900/50 dark:bg-gray-900/80`
    : "fixed inset-0 z-50 bg-gray-900/50 dark:bg-gray-900/80"
);

const footerClasses = computed(() =>
  isBottom.value
    ? "sticky bottom-0 pt-4 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] bg-white flex justify-center w-full"
    : "sticky bottom-0 p-4 bg-white flex justify-center w-full"
);

// Open state lives entirely in Vue reactivity: the panel and its backdrop are
// v-if children of <Transition>, so Vue owns every mount/unmount and no
// imperative library holds the node. Closing sets `open` false, the leave
// transition plays, and @after-leave emits hide-drawer for the parent to
// unmount — the node is always removed by the same system that created it.
const open = ref(false);
const panelRef = ref<HTMLElement | null>(null);
let previouslyFocused: HTMLElement | null = null;

// A sheet at bottom-0 sits over a scrollable board, and on a touch device a
// drag that starts outside the panel scrolls that board behind the scrim. The
// jQuery modal has always locked the body (via bootstrap's `modal-open`); the
// Drawer never did, which only became visible once it could be a sheet.
//
// This lock is NOT why the reschedule notify toggle is unreachable inside a
// bottom sheet, though it looks like a plausible culprit and was removed once on
// that theory. The scheduled-time editor is a ~707px tippy popper anchored
// ~535px down the viewport, so its footer simply does not fit below the anchor,
// and tippy re-anchors on scroll — page scrolling moves the popper with it, so
// removing this lock changes nothing. The fix belongs in that popper: clamp it
// to the visual viewport the way lib/anchoredPosition.ts already does for
// select panels. Don't trade this lock away for it.
const lockBodyScroll = () => {
  if (depth.drawers === 0) {
    depth.restoreOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  depth.drawers += 1;

  if (isBottom.value) {
    depth.sheets += 1;
    document.body.classList.add(SHEET_OPEN_CLASS);
  }
};

const unlockBodyScroll = () => {
  depth.drawers = Math.max(0, depth.drawers - 1);
  if (depth.drawers === 0) {
    document.body.style.overflow = depth.restoreOverflow ?? "";
    depth.restoreOverflow = null;
  }

  if (isBottom.value) {
    depth.sheets = Math.max(0, depth.sheets - 1);
    if (depth.sheets === 0) document.body.classList.remove(SHEET_OPEN_CLASS);
  }
};

const closeDrawer = () => {
  open.value = false;
};

const onClosed = () => emit("hide-drawer");

// Fourth dismissal path for a bottom sheet, alongside the close button, the
// backdrop and Escape. Routed through closeDrawer so it plays the same leave
// transition as the rest.
const { dragStyle, onTouchStart, onTouchMove, onTouchEnd } =
  useSwipeDismiss(closeDrawer);

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])"
].join(",");

// Visibility is checked without touching layout: `offsetParent` and
// `getBoundingClientRect` both report every element as hidden under jsdom,
// which would silently empty this list and disable the trap in every spec.
const isHidden = (el: HTMLElement): boolean =>
  !!el.closest("[hidden]") ||
  el.style.display === "none" ||
  el.style.visibility === "hidden";

const focusableInPanel = (): HTMLElement[] => {
  if (!panelRef.value) return [];
  return Array.from(
    panelRef.value.querySelectorAll<HTMLElement>(FOCUSABLE)
  ).filter((el) => !isHidden(el));
};

// aria-modal="true" is a promise to assistive tech that the rest of the page is
// inert. Without a trap it is only a claim: Tab walks straight out into the
// page behind the scrim, where a screen reader user has no way to tell they
// have left the dialog. Cycling at both ends keeps that promise.
const trapTab = (event: KeyboardEvent) => {
  const focusable = focusableInPanel();
  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement as HTMLElement | null;

  if (
    event.shiftKey &&
    (active === first || !panelRef.value?.contains(active))
  ) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape") return closeDrawer();
  if (event.key === "Tab") trapTab(event);
};

// Lets a parent that needs to close this drawer as a side effect of its own
// action (e.g. after a nested confirmation flow) go through the same leave
// transition as every other close path, instead of tearing the subtree down
// directly and skipping the animation.
defineExpose({ close: closeDrawer });

onMounted(async () => {
  document.addEventListener("keydown", onKeydown);
  lockBodyScroll();
  // Mount closed, then flip open so the slide-in transition plays from the
  // off-screen position instead of popping in already open.
  open.value = true;
  emit("show-drawer");

  // Move focus in once the panel exists, and remember where it came from. A
  // dialog that opens without taking focus leaves a keyboard user tabbing
  // through the page behind it.
  previouslyFocused = document.activeElement as HTMLElement | null;
  await nextTick();
  (focusableInPanel()[0] ?? panelRef.value)?.focus?.();
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeydown);
  unlockBodyScroll();
  // Return focus to whatever opened the drawer, so dismissing it does not dump
  // the caret back at the top of the document.
  previouslyFocused?.focus?.();
  previouslyFocused = null;
});
</script>

<style lang="scss">
.drawer.grid {
  grid-template-rows: 1fr 14fr 1fr;
}
/* A `vh` fallback for the `max-h-70dvh` utility on the panel, for browsers
   without `dvh` (added in Chrome 108; the app declares no support floor).
   Dropping the cap is not a graceful degradation: measured on Android Chrome 91
   with the software keyboard open, the panel grew to its full content height of
   788px inside a 403px viewport, putting the sheet's own title and search field
   333px ABOVE the top edge -- the user typing into an input they cannot see.
   This caps it at 321px there, with the search field visible again.

   It does not fight the utility, in either direction. A browser that supports
   `dvh` resolves both to the same 70dvh. One that does not discards `70dvh` as
   invalid at parse time -- the utility's declaration included -- so it never
   reaches the cascade, and the `70vh` below is the only cap left standing.
   Tailwind cannot express this itself: it gives no control over which of two
   arbitrary max-height utilities lands last in the generated CSS. */
.drawer--bottom {
  max-height: 70vh;
  max-height: 70dvh;
}
/* A `serviceshift-ui` Select inside a bottom sheet renders its menu with
   `body-render`, which teleports it to <body> to escape the sheet's
   max-height / `overflow-hidden` clipping. But that component hardcodes
   `z-index: 21` in its own stylesheet and exposes no prop for it, so once
   teleported the menu is a sibling of the z-[1003] sheet and paints UNDERNEATH
   it — escaping the clip only to become invisible. Playwright's toBeVisible()
   does not test occlusion, so this passes an escape assertion while being
   unusable; it has to be caught in a browser.
   1004 is the next value in the band the sheet reserves (1002-1008), so the
   menu clears the sheet while staying below SpinnerBackdrop (1009) and the
   dialogs above it. Gated on the body class so no other body-rendered Select
   anywhere in the app is affected. */
body.sheet-open .select-options {
  z-index: 1004;
}
.header-box-shadow {
  box-shadow: 0px -5px 11px -3px rgba(0, 0, 0, 0.15);
}
.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.3s ease-in-out;
}
.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(100%);
}
/* Two classes beat the one-class rule above, so bottom placement slides on the
   vertical axis while keeping the transition NAME that the teardown strand
   guard's diagnostics reference. */
.drawer-slide-enter-from.drawer--bottom,
.drawer-slide-leave-to.drawer--bottom {
  transform: translateY(100%);
}
.drawer-backdrop-enter-active,
.drawer-backdrop-leave-active {
  transition: opacity 0.3s ease;
}
.drawer-backdrop-enter-from,
.drawer-backdrop-leave-to {
  opacity: 0;
}
</style>
